import { useState } from 'react';
import { IconClose, IconCheck } from '@components/icons';
import { IconButton } from '@components/IconButton';
import { Button } from '@components/Button';
import styles from './EnrichmentJsonModal.module.scss';

export interface EnrichmentJsonModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Person's display name (shown in the modal title) */
  personName: string;
  /** The full enrichment data object to display as JSON */
  data: Record<string, unknown>;
}

const EnrichmentJsonContent = ({
  onClose,
  personName,
  data,
}: Omit<EnrichmentJsonModalProps, 'isOpen'>) => {
  const [copied, setCopied] = useState(false);

  const jsonString = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — user can still manually select and copy
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="enrichment-json-title"
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="enrichment-json-title" className={styles.title}>
            {personName}
          </h2>
          <IconButton
            size="small"
            aria-label="Close modal"
            onClick={onClose}
            className={styles.closeButton}
          >
            <IconClose size={18} />
          </IconButton>
        </div>

        {/* JSON Content */}
        <div className={styles.content}>
          <pre className={styles.jsonBlock}>
            <code>{jsonString}</code>
          </pre>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button
            variant="secondary"
            size="small"
            leadingIcon={
              copied ? (
                <IconCheck size={18} color="var(--color-semantic-success)" />
              ) : undefined
            }
            onClick={handleCopy}
          >
            {copied ? 'Copied!' : 'Copy JSON'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const EnrichmentJsonModal = ({
  isOpen,
  onClose,
  personName,
  data,
}: EnrichmentJsonModalProps) => {
  if (!isOpen) return null;
  return (
    <EnrichmentJsonContent onClose={onClose} personName={personName} data={data} />
  );
};
